<template>
  <div class="dropdown-container" ref="dropdown">
    <label style="text-align: left;" :for="id" v-if="label">{{ label }}
      <span v-if="required" style="color: red; font-size: 14px;">*</span>
    </label>

    <div class="custom-dropdown" :class="{ 'is-open': isOpen, 'is-invalid': showError }">
      <div class="dropdown-toggle" @click="toggleDropdown" :disabled="disabled" :style="{ width: width, height: height }">
        <span v-if="custom && isCustomInput" class="selected-text">
          <input
            type="number"
            v-model="customValue"
            @blur="updateCustomValue"
            @keyup.enter="updateCustomValue"
            placeholder="Enter custom value"
            class="custom-input"
          />
        </span>
        <span v-else class="selected-text">
          {{ selectedValue || placeholder }}
        </span>
        <i class="dropdown-arrow" :class="{ 'is-open': isOpen }"></i>
      </div>

      <div class="dropdown-menu" v-if="isOpen">
        <div v-if="search" class="search-dropdown">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search..."
            class="search-field"
            ref="searchInput"
          />
        </div>
        <div class="dropdown-items">
          <div v-for="option in filteredOptions" :key="option" class="dropdown-item"
            :class="{ 'selected': option === selectedValue }" @click.stop="selectOption(option)">
            {{ option }}
          </div>

          <div v-if="filteredOptions.length === 0" class="dropdown-item no-results">
            No results found
          </div>
        </div>
      </div>
    </div>

    <div v-if="showError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'InternalStateDropdown',
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: 'dropdown'
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: Number,
      default: 195
    },
    showError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: 'This field is required'
    },
    required: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '43px'
    },
    search: { 
      type: Boolean, 
      default: true 
    },
    custom: { 
      type: Boolean,
      default: false
    }
  },
  emits: ['change'],
  data() {
    return {
      isOpen: false,
      selectedValue: this.value,
      searchQuery: '',
      customValue: '',
      isCustomInput: false
    }
  },
  computed: {
    filteredOptions() {
      if (!this.search || !this.searchQuery) return this.options
      return this.options.filter(option =>
        option.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    }
  },
  watch: {
    value(newVal) {
      this.selectedValue = newVal;
    }
  },
  methods: {
    toggleDropdown() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen
        if (this.isOpen && this.search) {
          this.searchQuery = ''
          this.$nextTick(() => {
            this.$refs.searchInput.focus() 
          })
        }
        if (this.custom && this.isOpen && this.selectedValue === 'Custom Value') {
          this.isCustomInput = true;
          this.$nextTick(() => {
            const input = this.$el.querySelector('.custom-input');
            if (input) input.focus();
          });
        }
      }
    },
    selectOption(option) {
      if (option === 'Custom Value' && this.custom) {
        this.isCustomInput = true;
        this.$nextTick(() => {
          const input = this.$el.querySelector('.custom-input');
          if (input) input.focus();
        });
      } else {
        this.selectedValue = option;
        this.isCustomInput = false;
        this.customValue = '';
        this.$emit('change', this.selectedValue);
        this.isOpen = false;
      }
    },
    updateCustomValue() {
      if (this.custom && this.isCustomInput) {
        const value = this.customValue ? Number(this.customValue) : null;
        if (value !== null && !isNaN(value) && value > 0) {
          this.selectedValue = value.toString(); // Convert to string to match dropdown value type
          this.$emit('change', this.selectedValue);
        }
        this.isCustomInput = false;
        this.isOpen = false;
      }
    },
    handleClickOutside(event) {
      const dropdownEl = this.$refs.dropdown;
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.isOpen = false;
        if (this.custom && this.isCustomInput) {
          this.updateCustomValue();
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
.custom-dropdown {
  position: relative;
}
.custom-input {
  border: none !important;
  outline: none !important;
  min-width: 100% !important;
  min-width:210px !important;
  padding: 0 !important;
  margin-top: 8px;
  font-size: inherit;
}
</style>


// WEBPACK FOOTER //
// src/components/UIComponents/CustomDropdown.vue